import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, Sparkles, X, Trophy, Linkedin, Twitter, Link2, Check, Loader2 } from "lucide-react";
import { getAuthConfig } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const COLORS = ["#00F0FF", "#7000FF", "#FFC857", "#3DDC84", "#FF6B6B", "#FFFFFF"];

function ShareRow({ moduleId, shareState, copied, onLinkedIn, onTwitter, onCopy }) {
  if (!moduleId) return null;
  const loading = shareState === "loading";
  const errored = shareState === "error";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.4 }}
      className="mt-6 pt-5 border-t border-zinc-800/80"
      data-testid="celebration-share-row"
    >
      <p className="text-[11px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-3">
        Share my milestone
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={onLinkedIn}
          disabled={loading}
          data-testid="share-linkedin-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0A66C2] hover:bg-[#0B79DC] disabled:opacity-60 disabled:cursor-wait text-white font-outfit font-bold py-2.5 rounded-lg text-sm transition-colors"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Linkedin size={15} />}
          LinkedIn
        </button>
        <button
          onClick={onTwitter}
          disabled={loading}
          data-testid="share-twitter-btn"
          aria-label="Share on X"
          className="w-11 h-11 inline-flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 disabled:opacity-60 text-white rounded-lg transition-colors"
        >
          <Twitter size={15} />
        </button>
        <button
          onClick={onCopy}
          disabled={loading}
          data-testid="share-copy-btn"
          aria-label="Copy share link"
          className={`w-11 h-11 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-60 ${
            copied
              ? "bg-emerald-500/20 text-emerald-400"
              : "bg-zinc-800 hover:bg-zinc-700 text-white"
          }`}
        >
          {copied ? <Check size={15} /> : <Link2 size={15} />}
        </button>
      </div>
      <div className="h-4 mt-1.5">
        {copied && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[11px] text-emerald-400 font-dm-sans text-center"
            data-testid="share-copied-msg"
          >
            ✓ Link copied to clipboard
          </motion.p>
        )}
        {errored && (
          <p className="text-[11px] text-red-400 font-dm-sans text-center">
            Couldn't generate share link — please try again.
          </p>
        )}
      </div>
    </motion.div>
  );
}

function ConfettiPiece({ index }) {
  const random = useMemo(() => {
    const angle = Math.random() * Math.PI - Math.PI / 2; // upward bias
    const speed = 380 + Math.random() * 480;
    return {
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed - (200 + Math.random() * 220), // launch up
      rot: (Math.random() - 0.5) * 1080,
      size: 6 + Math.round(Math.random() * 8),
      shape: Math.random() > 0.55 ? "rect" : "circle",
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      delay: Math.random() * 0.18,
      duration: 1.6 + Math.random() * 1.4,
      gravityY: 700 + Math.random() * 250
    };
  }, []);

  return (
    <motion.span
      aria-hidden
      initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
      animate={{
        x: random.x,
        y: [0, random.y, random.y + random.gravityY],
        rotate: random.rot,
        opacity: [1, 1, 0]
      }}
      transition={{
        duration: random.duration,
        delay: random.delay,
        ease: ["easeOut", "easeIn"],
        times: [0, 0.45, 1]
      }}
      style={{
        position: "absolute",
        left: "50%",
        top: "55%",
        width: random.size,
        height: random.shape === "rect" ? Math.round(random.size * 0.45) : random.size,
        backgroundColor: random.color,
        borderRadius: random.shape === "circle" ? "50%" : "1px",
        boxShadow: `0 0 8px ${random.color}55`,
        pointerEvents: "none",
        willChange: "transform, opacity"
      }}
      data-testid={`confetti-piece-${index}`}
    />
  );
}

export default function ModuleUnlockCelebration({
  open,
  moduleId,
  moduleTitle,
  nextModule,
  onClose,
  onContinue
}) {
  const [shareState, setShareState] = useState("idle"); // idle | loading | ready | error
  const [shareData, setShareData] = useState(null); // { share_url, image_url, linkedin_url, twitter_url }
  const [copied, setCopied] = useState(false);

  // Reset share state when the celebration opens for a new module
  useEffect(() => {
    if (open) {
      setShareState("idle");
      setShareData(null);
      setCopied(false);
    }
  }, [open, moduleId]);

  // Lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Auto-dismiss after 14s — slightly longer to give space for sharing
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 14000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const ensureShareData = async () => {
    if (shareData) return shareData;
    if (!moduleId) return null;
    setShareState("loading");
    try {
      const res = await axios.post(
        `${API}/python-lab/milestones`,
        { module_id: moduleId },
        getAuthConfig()
      );
      setShareData(res.data);
      setShareState("ready");
      return res.data;
    } catch (e) {
      console.error("Milestone share failed:", e);
      setShareState("error");
      return null;
    }
  };

  const openInPopup = (url) => {
    window.open(
      url,
      "_blank",
      "noopener,noreferrer,width=620,height=720"
    );
  };

  const writeToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch { /* fall through to legacy path */ }
    }
    // Legacy fallback for restricted contexts
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleShareLinkedIn = async () => {
    const data = await ensureShareData();
    if (!data) return;
    // Copy share URL too — many users paste it into a custom post
    if (await writeToClipboard(data.share_url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
    openInPopup(data.linkedin_url);
  };

  const handleShareTwitter = async () => {
    const data = await ensureShareData();
    if (!data) return;
    openInPopup(data.twitter_url);
  };

  const handleCopy = async () => {
    const data = await ensureShareData();
    if (!data) return;
    if (await writeToClipboard(data.share_url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } else {
      // Surface as a soft error
      setShareState("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          data-testid="module-unlock-celebration"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={onClose}
            aria-label="Close celebration"
          />

          {/* Confetti layer (centered, absolute) */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 70 }).map((_, i) => (
              <ConfettiPiece key={i} index={i} />
            ))}
          </div>

          {/* Card */}
          <motion.div
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22, delay: 0.05 }}
            className="relative max-w-md w-full bg-gradient-to-br from-zinc-900 to-zinc-950 border border-[#00F0FF]/40 rounded-3xl p-8 sm:p-10 shadow-[0_0_60px_rgba(0,240,255,0.25)]"
          >
            <button
              onClick={onClose}
              data-testid="celebration-close-btn"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={14} />
            </button>

            {/* Trophy badge with subtle glow pulse */}
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
              className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center relative"
            >
              <motion.div
                animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 rounded-2xl bg-[#00F0FF]/40"
              />
              <Trophy size={34} className="text-black relative z-10" />
            </motion.div>

            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.4 }}
                className="inline-flex items-center gap-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-full px-3 py-1 text-[10px] font-mono font-bold text-[#00F0FF] uppercase tracking-wider mb-4"
              >
                <Sparkles size={11} /> Module Mastered
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.45 }}
                className="font-outfit font-black text-2xl sm:text-3xl text-white mb-2 leading-tight"
              >
                You finished
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.45 }}
                className="font-outfit font-black text-3xl sm:text-4xl bg-gradient-to-r from-[#00F0FF] to-[#7000FF] bg-clip-text text-transparent mb-5"
                data-testid="celebration-module-title"
              >
                {moduleTitle || "the module"}!
              </motion.p>

              {nextModule ? (
                <>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                    className="text-sm text-zinc-400 font-dm-sans mb-7 leading-relaxed"
                  >
                    Next up:{" "}
                    <span className="text-white font-bold">{nextModule.title}</span>{" "}
                    is now unlocked. Keep the streak alive.
                  </motion.p>
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onContinue}
                    data-testid="celebration-continue-btn"
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-black py-3.5 rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2 text-base"
                  >
                    Start {nextModule.title}
                    <ArrowRight size={18} />
                  </motion.button>
                  <button
                    onClick={onClose}
                    data-testid="celebration-stay-btn"
                    className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 font-dm-sans transition-colors"
                  >
                    Take a breather first
                  </button>

                  <ShareRow
                    moduleId={moduleId}
                    shareState={shareState}
                    copied={copied}
                    onLinkedIn={handleShareLinkedIn}
                    onTwitter={handleShareTwitter}
                    onCopy={handleCopy}
                  />
                </>
              ) : (
                <>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                    className="text-sm text-zinc-400 font-dm-sans mb-7 leading-relaxed"
                  >
                    That was the final module. You just completed the entire Python Lab curriculum. Pro mode unlocked.
                  </motion.p>
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    data-testid="celebration-finish-btn"
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-black py-3.5 rounded-xl hover:brightness-110 transition-all"
                  >
                    Awesome
                  </motion.button>

                  <ShareRow
                    moduleId={moduleId}
                    shareState={shareState}
                    copied={copied}
                    onLinkedIn={handleShareLinkedIn}
                    onTwitter={handleShareTwitter}
                    onCopy={handleCopy}
                  />
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
